import { Controller } from "@hotwired/stimulus"

// Cmd+K / Ctrl+K palette. Two layers:
//   1. Static nav links — quick "jump to a section", filtered client-side.
//   2. Live console-wide search — Brain, CRM (companies/people/deals), events,
//      and your own tasks, fetched from /search as you type.
// Keyboard nav (arrows + Enter) runs across whatever is visible from both.
export default class extends Controller {
  static targets = ["overlay", "dialog", "input", "list", "item", "empty", "results"]
  static values = { searchUrl: String }

  globalKey(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault()
      this.toggle()
    } else if (event.key === "Escape" && this.isOpen()) {
      this.close()
    }
  }

  isOpen() {
    return !this.overlayTarget.classList.contains("hidden")
  }

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("flex")
    this.inputTarget.value = ""
    this.clearResults()
    this.filter()
    this.inputTarget.focus()
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex")
  }

  backdropClose(event) {
    if (!this.dialogTarget.contains(event.target)) this.close()
  }

  filter() {
    const raw = this.inputTarget.value.trim()
    const query = raw.toLowerCase()
    // Filter the static nav links (quick jump-to).
    this.staticItems().forEach((item) => {
      item.classList.toggle("hidden", !item.textContent.toLowerCase().includes(query))
      item.classList.remove("nav-item-active")
    })
    // Live console-wide search kicks in at 2+ characters.
    if (raw.length >= 2) {
      this.scheduleFetch(raw)
    } else {
      this.clearResults()
    }
    this.refreshEmpty()
    this.highlightIndex = 0
    this.applyHighlight()
  }

  scheduleFetch(query) {
    clearTimeout(this.fetchTimer)
    this.fetchTimer = setTimeout(() => this.fetchResults(query), 160)
  }

  async fetchResults(query) {
    if (!this.hasResultsTarget || !this.searchUrlValue) return
    const url = `${this.searchUrlValue}?palette=1&q=${encodeURIComponent(query)}`
    this.seq = (this.seq || 0) + 1
    const mySeq = this.seq
    try {
      const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      if (mySeq !== this.seq) return // a newer keystroke superseded this fetch
      this.resultsTarget.innerHTML = res.ok ? await res.text() : ""
    } catch (_e) {
      this.resultsTarget.innerHTML = ""
    }
    this.refreshEmpty()
    this.highlightIndex = 0
    this.applyHighlight()
  }

  clearResults() {
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
  }

  refreshEmpty() {
    if (!this.hasEmptyTarget) return
    // The results partial renders its own "No results" line at 2+ chars, so the
    // palette's static "No matches" only applies to short jump-to queries.
    const hide = this.visibleItems().length > 0 || this.inputTarget.value.trim().length >= 2
    this.emptyTarget.classList.toggle("hidden", hide)
  }

  navigate(event) {
    const visible = this.visibleItems()
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightIndex = Math.min((this.highlightIndex || 0) + 1, visible.length - 1)
      this.applyHighlight()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightIndex = Math.max((this.highlightIndex || 0) - 1, 0)
      this.applyHighlight()
    } else if (event.key === "Enter") {
      event.preventDefault()
      const target = visible[this.highlightIndex || 0]
      if (target) target.click()
    }
  }

  staticItems() {
    return this.itemTargets.filter((item) => !this.isResult(item))
  }

  isResult(item) {
    return this.hasResultsTarget && this.resultsTarget.contains(item)
  }

  visibleItems() {
    return this.itemTargets.filter((item) => !item.classList.contains("hidden"))
  }

  applyHighlight() {
    this.visibleItems().forEach((item, index) => {
      item.classList.toggle("nav-item-active", index === (this.highlightIndex || 0))
    })
  }
}
